import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, DollarSign } from "lucide-react";
import { api } from "@/lib/api";
import { fileUrl } from "@/components/ImageUpload";

const DIVS = ["Youth In-Hand", "Adult In-Hand", "Adult Undersaddle"];
const money = (n) => `$${Number(n).toLocaleString()}`;

const Thumb = ({ h }) => (
  <div className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-lg bg-white/15">
    {h.current_photos?.[0] ? <img src={fileUrl(h.current_photos[0])} alt={h.horse_name} className="h-full w-full object-cover" /> : <Trophy size={16} className="text-secondary" />}
  </div>
);

const HighlightRow = ({ h, label, value, testid }) => (
  <Link to={`/mustangs/${h.id}`} data-testid={testid} className="flex items-center gap-3 rounded-xl bg-white/10 p-2 transition-colors hover:bg-white/20">
    <Thumb h={h} />
    <div className="min-w-0 flex-1">
      <p className="text-[10px] font-bold uppercase tracking-wider text-secondary">{label}</p>
      <p className="truncate font-display text-base font-bold leading-tight">{h.horse_name}</p>
      <p className="truncate text-xs text-white/70">HIP #{h.hip_number} · {h.trainer_name}</p>
    </div>
    {value && <span className="font-display text-base font-bold tabular-nums">{value}</span>}
  </Link>
);

// Champions (placing 1 per division) and the top sale for an archived year, from the whole year's list.
export const YearHighlights = ({ year }) => {
  const [horses, setHorses] = useState([]);
  useEffect(() => {
    api.get("/horses", { params: { year } }).then((r) => setHorses(r.data)).catch(() => setHorses([]));
  }, [year]);

  const champs = DIVS.flatMap((d) => horses.filter((h) => h.division === d && h.archive_snapshot?.placing === 1));
  const sold = horses.filter((h) => h.sale_price != null);
  const top = sold.reduce((a, h) => (!a || h.sale_price > a.sale_price ? h : a), null);
  if (!champs.length && !top) return null;
  return (
    <div className="px-4 pb-4" data-testid="year-highlights">
      <div className="animate-fade-up rounded-2xl bg-primary p-4 text-white shadow-md">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-secondary">{year} Highlights</p>
        {champs.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {champs.map((h) => (
              <HighlightRow key={h.id} h={h} testid={`year-champion-${h.hip_number}`}
                label={`${h.division} Champion${h.archive_snapshot?.tied ? " (tie)" : ""}`} value={h.archive_snapshot?.overall_total} />
            ))}
          </div>
        )}
        {top && (
          <div className="mt-3 border-t border-white/15 pt-3">
            <HighlightRow h={top} testid="year-top-sale" label="Top Sale" value={money(top.sale_price)} />
            <p className="mt-2 flex items-center gap-1 text-xs text-white/70" data-testid="year-sales-summary">
              <DollarSign size={12} /> {sold.length} sold · {money(sold.reduce((s, h) => s + h.sale_price, 0))} total · {money(Math.round(sold.reduce((s, h) => s + h.sale_price, 0) / sold.length))} average
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
